/**
 * PHOTON-80 — live data access for the Executive Dashboard.
 *
 * One call to crm-service's aggregate endpoint returns every pillar, the
 * ranked action list, and the cached "Photon's Read" commentary. The server
 * computes all figures, severities, and display strings (single thresholds
 * module lives there — src/services/executive_thresholds.py); this module
 * only scales raw sparkline history onto the SVG grid.
 */

import { useQuery, UseQueryResult } from "@tanstack/react-query";

import useCrmApiClient from "../../../hooks/useCrmApiClient";
import { ActionItem, Narrative, PillarData } from "./types";

/** Pillar as the API emits it: raw history series instead of sparkY. */
export interface ApiPillar extends Omit<PillarData, "sparkY"> {
  history?: number[];
}

export interface ExecutiveSummary {
  generatedAtUtc: string;
  pillars: ApiPillar[];
  actions: ActionItem[];
  narrative: Narrative | null;
}

/** Map raw history values onto the sparkline's 0-46 viewBox (y down). */
export function scaleHistory(history?: number[]): number[] | undefined {
  if (!history || history.length < 2) return undefined;
  const min = Math.min(...history);
  const max = Math.max(...history);
  if (max === min) return history.map(() => 23);
  // Pad to y ∈ [6, 40] so the endpoint dot never clips.
  return history.map((v) => 40 - ((v - min) / (max - min)) * 34);
}

export function toPillarData(pillar: ApiPillar): PillarData {
  const { history, ...rest } = pillar;
  return { ...rest, sparkY: scaleHistory(history) };
}

export function useExecutiveSummary(): UseQueryResult<ExecutiveSummary, Error> {
  const api = useCrmApiClient();
  return useQuery<ExecutiveSummary, Error>({
    queryKey: ["executive-summary"],
    queryFn: () => api.get<ExecutiveSummary>("/api/executive/summary"),
    // The narrative is cached server-side; refetching is cheap for the
    // portal but fans out to QBO/GHL upstream — keep a sane floor.
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 1,
    enabled: api.isConfigured(),
  });
}
